import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-light-svg-icons/faPencil';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { noop } from 'ui-components/default-props/noop';

import { messages } from './messages';
import type { InlineEditProps } from './inline-edit-types';

import styles from './inline-edit.module.less';

export const InlineEdit = ({ value, onConfirm, renderReadView, renderEditView }: InlineEditProps) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const editWrapperRef = useRef<HTMLDivElement>(null);
  const isJustEnteredEditRef = useRef(false);

  const enterEditMode = useCallback(() => {
    setDraftValue(value);
    setIsEditing(true);
    isJustEnteredEditRef.current = true;
    setTimeout(() => {
      isJustEnteredEditRef.current = false;
    }, 0);
  }, [value]);

  const confirm = useCallback(
    (draft: string) => {
      setIsEditing(false);
      if (draft !== value) {
        onConfirm(draft);
      }
    },
    [value, onConfirm],
  );

  const cancel = useCallback(() => {
    setIsEditing(false);
    setDraftValue(value);
  }, [value]);

  const handleWrapperBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (isJustEnteredEditRef.current) {
        return;
      }
      const focusMovedOutside = !editWrapperRef.current?.contains(event.relatedTarget as Node);
      if (focusMovedOutside) {
        confirm(draftValue);
      }
    },
    [confirm, draftValue],
  );

  const inputProps = useMemo(
    () => ({
      value: draftValue,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => setDraftValue(event.target.value),
      onBlur: noop,
      onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          confirm(draftValue);
        } else if (event.key === 'Escape') {
          cancel();
        }
      },
      autoFocus: true,
    }),
    [draftValue, confirm, cancel],
  );

  if (isEditing) {
    return (
      <div className={styles['edit-wrapper']} onBlur={handleWrapperBlur} ref={editWrapperRef}>
        {renderEditView({ inputProps })}
      </div>
    );
  }

  return (
    <Button
      ariaLabel={intl.formatMessage(messages.clickToEdit)}
      className={styles['read-wrapper']}
      data-ignore-root-event-listeners
      onClick={enterEditMode}
      theme={THEME_CONSTANTS.unstyled}
    >
      {renderReadView({ value })}
      <FontAwesomeIcon className={styles['pencil-icon']} icon={faPencil} />
    </Button>
  );
};
